import { Fragment, useContext } from 'react';
import { Button, Dialog, LabeledContainer } from '@schibsted-svp/react-ui';
import { useFormikContext } from 'formik';

import { Select } from '../../../../Form/Select/Select';
import { CategoriesSelect } from '../../../../Form/CategoriesSelect/CategoriesSelect';
import { FormValuesProvider } from '../../../../Form/FormValuesProvider/FormValuesProvider';
import { SubmitButton } from '../../../../Form/SubmitButton/SubmitButton';
import { Form } from '../../../../Form/Form';
import { PageContext } from '../../../../../contexts/PageContext';
import { TagsSelect } from '../../../../Form/TagsSelect/TagsSelect';
import { Input } from '../../../../Form/Input/Input';
import { getProvider } from '../../../../../services/svpProvider';
import { config } from '../../../../../config';

import { EndpointItemsSlider } from './EndpointItemsSlider/EndpointItemsSlider';
import { UsePageTaxonomyCheckbox } from './UsePageTaxonomyCheckbox/UsePageTaxonomyCheckbox';
import { SubscriptionsCheckbox } from './SubscriptionsCheckbox/SubscriptionsCheckbox';
import { UpcomingAssetsCheckbox } from './UpcomingAssetsCheckbox/UpcomingAssetsCheckbox';
import { StreamTypeSelect } from './StreamTypeSelect';

import type { ComponentResponse, PageResponse } from '../../../../../types';
import type { ReactNode } from 'react';
import type { FormikHelpers } from 'formik';

const provider = getProvider();
const blendOptions =
  config.curate.blendApi.providers[provider]?.blendOptions || [];

type EndpointParams = Record<string, unknown>;

type Props = {
  endpoint: string;
  dataType: string;
  params?: EndpointParams;
  onSubmit: (params: EndpointParams) => void;
  onCancel?: () => void;
};

function EndpointParamsForm({
  dataType,
  endpoint,
  params,
  onSubmit,
  onCancel,
}: Props) {
  const { values, setFieldValue } = useFormikContext<ComponentResponse>();
  const pageContextValue = useContext(PageContext);
  const page: Partial<PageResponse> = pageContextValue?.page ?? {};

  const component = values;

  const isShared = component.isShared && page.type === undefined;

  const canUsePageTaxonomy =
    isShared ||
    (page.type !== 'custom' &&
      (['list', 'slider', 'editorial'] as string[]).includes(component.type));

  const isTemplate = page.type === 'template';

  const hasCategoryTaxonomy =
    page.type === 'category' || (isTemplate && page.forType === 'category');

  const hasTagTaxonomy =
    page.type === 'tag' || (isTemplate && page.forType === 'tag');

  const usePageTaxonomy =
    component.id !== undefined ||
    (component.options && component.options.usePageTaxonomy !== undefined) // so we can change this option for a new component with no id
      ? component.options.usePageTaxonomy
      : true; // since this defaults to true for new components

  const isUsingPageCategory = hasCategoryTaxonomy && usePageTaxonomy;
  const isUsingPageTag = hasTagTaxonomy && usePageTaxonomy;

  function renderCategoriesSelect(name: string, label: string): ReactNode {
    return (
      <Fragment>
        <CategoriesSelect
          label={label}
          name={name}
          disabled={isUsingPageCategory}
        />
        {canUsePageTaxonomy && hasCategoryTaxonomy && (
          <UsePageTaxonomyCheckbox
            taxonomy={'category'}
            checked={Boolean(usePageTaxonomy)}
            setFieldValue={
              setFieldValue as FormikHelpers<unknown>['setFieldValue']
            }
          />
        )}
      </Fragment>
    );
  }

  return (
    <Form
      initialValues={params}
      onSubmit={async (values) => {
        onSubmit(values);
      }}
    >
      <Dialog.Section>
        {isShared && (
          <UsePageTaxonomyCheckbox
            taxonomy={'taxonomy'}
            checked={Boolean(usePageTaxonomy)}
            setFieldValue={
              setFieldValue as FormikHelpers<unknown>['setFieldValue']
            }
          />
        )}
        {endpoint === 'svp/assets' && (
          <Fragment>
            <StreamTypeSelect />
            {renderCategoriesSelect('filter.categoryId', 'Category')}
            <TagsSelect
              isMulti
              label="Tag"
              name="filter.tag"
              disabled={isUsingPageTag}
            />
            {canUsePageTaxonomy && hasTagTaxonomy && (
              <UsePageTaxonomyCheckbox
                taxonomy={'tag'}
                checked={Boolean(usePageTaxonomy)}
                setFieldValue={
                  setFieldValue as FormikHelpers<unknown>['setFieldValue']
                }
              />
            )}
          </Fragment>
        )}
        {endpoint === 'svp/search' && dataType !== 'stories' && (
          <Fragment>
            <StreamTypeSelect />
            {renderCategoriesSelect('filter.categoryId', 'Category')}
            <TagsSelect
              isMulti
              label="Tag"
              name="filter.tagsid"
              disabled={isUsingPageTag}
            />
            {canUsePageTaxonomy && hasTagTaxonomy && (
              <UsePageTaxonomyCheckbox
                taxonomy={'tag'}
                checked={Boolean(usePageTaxonomy)}
                setFieldValue={
                  setFieldValue as FormikHelpers<unknown>['setFieldValue']
                }
              />
            )}
            <LabeledContainer label="Required subscriptions">
              <SubscriptionsCheckbox name="filter.access" />
            </LabeledContainer>
            <UpcomingAssetsCheckbox name="filter.flightTimesStart" />
          </Fragment>
        )}
        {endpoint === 'svp/search' && dataType === 'stories' && (
          <Fragment>
            {renderCategoriesSelect('filter.categoryId', 'Category')}
            <TagsSelect
              isMulti
              label="Tag"
              name="filter.tagsid"
              disabled={isUsingPageTag}
            />
          </Fragment>
        )}
        {endpoint === 'svp/most-seen' && (
          <Fragment>
            <Select
              label="Interval"
              name="interval"
              options={[
                { value: 'hour', label: 'Last hour' },
                { value: 'day', label: 'Last 24 hours' },
                { value: 'week', label: 'This week' },
                { value: 'month', label: 'This month' },
              ]}
            />
            {renderCategoriesSelect('filter.categoryId', 'Category')}
          </Fragment>
        )}
        {endpoint === 'svp/categories' && (
          <Fragment>
            {renderCategoriesSelect('filter.parentId', 'Parent category')}
          </Fragment>
        )}
        {endpoint === '/profiles' &&
          renderCategoriesSelect('categoryId', 'Related category')}
        {endpoint === 'curate/blend' && (
          <>
            <Input name="blendId" label="Blend ID" />
            <Select
              name="blendId"
              label="Blend ID"
              placeholder="Select a blend"
              options={blendOptions}
            />
          </>
        )}
        <LabeledContainer label="Preview">
          <FormValuesProvider
            component={EndpointItemsSlider}
            endpoint={endpoint}
            dataType={dataType}
            valuesPropName="params"
          />
        </LabeledContainer>
      </Dialog.Section>
      <Dialog.Section variant="darker" mode="flexRight">
        <Button type="button" onClick={onCancel} variant="standard">
          Cancel
        </Button>
        <SubmitButton>Set</SubmitButton>
      </Dialog.Section>
    </Form>
  );
}

export { EndpointParamsForm };
